import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  fetchExtractionCells,
  fetchExtractionCellById,
  fetchEnvCellRoutesForExtractionCell,
  createExtractionCell,
  updateExtractionCell,
} from '_/api/extractionCells/extractionCells.api';
import { IExtractionCellForm } from '_/types/extractionCells/extractionCells.types';
import { notification } from 'antd';

export const EXTRACTION_CELLS_QUERY_KEY = ['extractionCells'];
export const EXTRACTION_CELL_QUERY_KEY = (id: string): string[] => ['extractionCell', id];
export const ENV_CELL_ROUTES_QUERY_KEY = (extractionCellId: string): string[] => ['envCellRoutes', extractionCellId];

export const useExtractionCells = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: EXTRACTION_CELLS_QUERY_KEY,
    queryFn: fetchExtractionCells,
  });
};

export const useExtractionCell = (id: string, enabled = true): UseQueryResult<any> => {
  return useQuery({
    queryKey: EXTRACTION_CELL_QUERY_KEY(id),
    queryFn: () => fetchExtractionCellById(id),
    enabled: enabled && !!id,
  });
};

export const useEnvCellRoutesForExtractionCell = (extractionCellId: string, enabled = false): UseQueryResult<any> => {
  return useQuery({
    queryKey: ENV_CELL_ROUTES_QUERY_KEY(extractionCellId),
    queryFn: () => fetchEnvCellRoutesForExtractionCell(extractionCellId),
    enabled: enabled && !!extractionCellId,
  });
};

export const useCreateExtractionCell = (): UseMutationResult<any, Error, Partial<IExtractionCellForm>> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: (data: Partial<IExtractionCellForm>) => createExtractionCell(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EXTRACTION_CELLS_QUERY_KEY });
      notification.success({
        message: 'Extraction Cell Created',
      });
      void history({ to: '/extractioncells' });
    },
    onError: (error: any) => {
      console.error('Error creating extraction cell', error);
      notification.error({
        message: error?.messages?.[0] || 'Error creating extraction cell',
      });
    },
  });
};

export const useUpdateExtractionCell = (): UseMutationResult<any, Error, { id: string; data: Partial<IExtractionCellForm> }> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<IExtractionCellForm> }) => updateExtractionCell(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: EXTRACTION_CELLS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: EXTRACTION_CELL_QUERY_KEY(variables.id) });
      notification.success({
        message: 'Extraction Cell Saved',
      });
      void history({ to: '/extractioncells' });
    },
    onError: (error: any) => {
      console.error('Error updating extraction cell', error);
      notification.error({
        message: error?.messages?.[0] || 'Error updating extraction cell',
      });
    },
  });
};
